import { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Hotel, FileText, MessageSquareQuote, Users, Settings as SettingsIcon, BarChart3, ImageIcon, HelpCircle, MapPinned, LogOut, Home as HomeIcon, Handshake, UserCog, FilePlus, FolderOpen, ListTree, Gauge, History, KeyRound, Calculator, Scale, ClipboardCheck, HardHat, LineChart } from "lucide-react";
import { useAuth } from "@/lib/auth";
import ChangePasswordDialog from "@/pages/admin/ChangePasswordDialog";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/pages/home", icon: HomeIcon, label: "Home Page" },
  { to: "/admin/pages/partner", icon: Handshake, label: "Partner Page" },
  { to: "/admin/pages/revenue-calculator", icon: Calculator, label: "Revenue Calc" },
  { to: "/admin/pages/staffing-calculator", icon: HardHat, label: "Staffing Calc" },
  { to: "/admin/analytics", icon: LineChart, label: "Analytics" },
  { to: "/admin/pages/partnership-models", icon: Scale, label: "Partnership Models" },
  { to: "/admin/property-readiness", icon: ClipboardCheck, label: "Property Readiness" },
  { to: "/admin/properties", icon: Hotel, label: "Properties" },
  { to: "/admin/pages", icon: FilePlus, label: "Pages" },
  { to: "/admin/seo-pages", icon: MapPinned, label: "SEO Pages" },
  { to: "/admin/blog", icon: FileText, label: "Blog & Stories" },
  { to: "/admin/leads", icon: Users, label: "Leads" },
  { to: "/admin/testimonials", icon: MessageSquareQuote, label: "Testimonials" },
  { to: "/admin/gallery", icon: ImageIcon, label: "Gallery" },
  { to: "/admin/media", icon: FolderOpen, label: "Media Library" },
  { to: "/admin/faqs", icon: HelpCircle, label: "FAQs" },
  { to: "/admin/health", icon: Gauge, label: "Health", adminOnly: true },
  { to: "/admin/activity", icon: History, label: "Activity", adminOnly: true },
  { to: "/admin/menu", icon: ListTree, label: "Menu", adminOnly: true },
  { to: "/admin/users", icon: UserCog, label: "Users & Roles", adminOnly: true },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings", adminOnly: true },
  { to: "/admin/tracking", icon: BarChart3, label: "Tracking", adminOnly: true },
];

const ROLE_BADGE = {
  admin: "bg-emerald-100 text-emerald-700",
  editor: "bg-sky-100 text-sky-700",
  viewer: "bg-zinc-100 text-zinc-600",
};

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [pwOpen, setPwOpen] = useState(false);

  if (loading) return <div className="p-12">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  // Guard admin-only routes for editors/viewers
  const ADMIN_ONLY_PATHS = ["/admin/users", "/admin/settings", "/admin/tracking", "/admin/health", "/admin/activity"];
  const isAdminOnlyPath = ADMIN_ONLY_PATHS.some((p) => loc.pathname.startsWith(p));
  if (isAdminOnlyPath && user.role !== "admin") {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900 font-sans">
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <div className="flex">
        <aside className="hidden md:flex flex-col w-64 min-h-screen bg-white border-r border-zinc-200 sticky top-0" data-testid="admin-sidebar">
          <div className="p-6 border-b border-zinc-200">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Happy Retreats</div>
            <div className="font-display text-2xl mt-1">Admin</div>
            {user && (
              <div className="mt-3 flex items-center flex-wrap gap-x-2 gap-y-1" data-testid="admin-user-badge">
                <span className="text-xs text-zinc-700 truncate max-w-[140px]" title={user.email}>{user.name || user.email}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${ROLE_BADGE[user.role] || "bg-zinc-100 text-zinc-600"}`}>{user.role || "user"}</span>
              </div>
            )}
          </div>
          <nav className="flex-1 p-3 space-y-0.5">
            {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
                  data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{n.label}</span>
                </NavLink>
              );
            })}
          </nav>
          <div className="p-3 border-t border-zinc-200 space-y-0.5">
            <button
              onClick={() => setPwOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-700 hover:bg-zinc-100"
              data-testid="admin-change-password"
            >
              <KeyRound className="w-4 h-4" /> Change password
            </button>
            <button
              onClick={async () => { await logout(); nav("/admin/login"); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-700 hover:bg-zinc-100"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="p-6 md:p-10 max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
